import React from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CustomText } from "../../../components/index";
import { Grid } from "@material-ui/core";
import { hooks, masks, validations } from "../../../utils";
import { fonts } from "../../../config";
import Styles from "../styles/Styles";
import { customModal } from "../../../components/modals/utils";
import { ManagerProps } from "../managerRegistryPage";
import { api } from "../../../services";
import { StorageContext } from "../../../contexts/StorageContext";
import { Input, InputTags } from "../../../components/inputs/newForm";
import { PlaceOption } from "../../../utils/hooks";

export interface Owners {
  label: string;
  value: string;
  places: {
    label: string;
    value: string;
  }[];
}

interface ListStudios {
  label: string;
  value: string;
}

export interface DataRegisterProps {
  id: string;
  name: string;
  email: string;
  cpf: string;
  studios: ListStudios[];
  owners: Owners[];
}

export interface DataEditProps {
  id: string;
  name: string;
  cpf: string;
  studios: ListStudios[];
  owners: Owners[];
}

const managerBaseSchema = z.object({
  fullName: z
    .string()
    .min(1, "*Campo Obrigatório")
    .refine((v) => validations.isValidFullname(v) === null, {
      message: "O campo deve conter um nome completo valido.",
    }),
  cpf: z
    .string()
    .min(1, "*Campo Obrigatório")
    .refine((v) => validations.validateCPF(v) === null, {
      message: "O CPF digitado não é válido",
    }),
  email: z
    .string()
    .min(1, "*Campo Obrigatório")
    .refine((v) => validations.isEmailValid(v) === null, {
      message: "O campo deve conter um e-mail válido.",
    }),
  linkToOwner: z.array(z.object({ value: z.string(), label: z.string() })),
  linkToStudios: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .min(1, "*Campo Obrigatório"),
});

type ManagerFormData = z.infer<typeof managerBaseSchema>;

function ModalRegistry({
  data,
  ownersList,
  isManager,
  filterManager,
  viewOnly,
  placeList
}: {
  data?: any;
  ownersList: any[];
  isManager: boolean;
  filterManager?: () => void;
  viewOnly?: boolean;
  placeList?: PlaceOption[]
}) {
  const { loading, call } = hooks.useRequest();

  const selectedOwners = isManager ? ownersList : data?.users || [];
  console.log("places", placeList);
  const placesFormatted =
    data?.places?.map((place) => ({
      label: place.name,
      value: place.id,
    })) || [];

  console.log(data?.users, ownersList)
  const userFormatted =
    data?.users?.map((owner) => ({
      label: owner.name + " - " + owner.email,
      value: owner.id,
    })).filter((a) => ownersList.find((b) => b.value === a.value)) || [];

  const managerSchema = React.useMemo(
    () =>
      managerBaseSchema.superRefine((data, ctx) => {
        if (
          !isManager &&
          (!data.linkToOwner || data.linkToOwner.length === 0)
        ) {
          ctx.addIssue({
            code: "custom",
            path: ["linkToOwner"],
            message: "*Campo Obrigatório",
          });
        }
      }),
    [isManager]
  );

  const methods = useForm<ManagerFormData>({
    resolver: zodResolver(managerSchema),
    defaultValues: {
      fullName: data?.name || "",
      cpf: data?.cpf ? masks.CPF(data.cpf) : "",
      email: data?.email || "",
      linkToOwner: data?.users ? userFormatted : [],
      linkToStudios: data ? placesFormatted : [],
    },
  });

  const { control, handleSubmit, setValue, getValues, watch } = methods;

  const watchedOwners = watch("linkToOwner");
  const hasOwnerSelected = (watchedOwners ?? []).length > 0;
  const filteredPlaces = React.useMemo(() => {
    const basePlaces = placeList ?? [];
    if (isManager) return basePlaces;
    if (!hasOwnerSelected) return [];

    const selectedIds = new Set((watchedOwners ?? []).map((o: any) => o.value));
    console.log(ownersList
      .filter((o) => selectedIds.has(o.value)))
    const ownerPlaceIds = new Set(
      ownersList
        .filter((o) => selectedIds.has(o.value))
        .flatMap((o) => (o.places ?? []).map((p: any) => p.value))
    );
    return basePlaces.filter((place: any) => ownerPlaceIds.has(place.value));
  }, [isManager, placeList, watchedOwners, hasOwnerSelected, ownersList]);

  const handleOwnerChange = (value: any[]) => {
    const currentStudios = getValues("linkToStudios") as ListStudios[];

    if (value && value.length > 0) {
      const selectedIds = new Set(value.map((o: any) => o.value));
      const ownerPlaceIds = new Set(
        ownersList
          .filter((o) => selectedIds.has(o.value))
          .flatMap((o) => (o.places ?? []).map((p: any) => p.value))
      );
      const basePlaces = placeList ?? [];
      const availableIds = new Set(
        basePlaces
          .filter((place: any) => ownerPlaceIds.has(place.value))
          .map((place: any) => place.value)
      );
      const kept = currentStudios?.filter((s) => availableIds.has(s.value)) ?? [];
      setValue("linkToStudios", kept);
    } else {
      setValue("linkToStudios", []);
    }
  };

  const onSubmit = (values: ManagerFormData) => {
    let submitData = {
      id: data ? data.id : "",
      cpf: masks.removeNumberMask(values.cpf),
      email: values.email,
      name: values.fullName,
      studiosIds: (values.linkToStudios ?? [])
        .map((studio) => studio.value)
        .filter((id) => id !== "all"),
      usersIds: isManager
        ? selectedOwners?.map((owner) => owner.value)
        : (values.linkToOwner ?? [])
          .map((owner) => owner.value)
          .filter((id) => id !== "all"),
    };

    if (data) {
      call(null, api.putManagers(submitData), (response) => {
        if (response.ok) {
          filterManager && filterManager();
          customModal.close();
        }
      });
    } else {
      call(null, api.postManagers(submitData), (response) => {
        if (response.ok) {
          customModal.close();
        }
      });
    }
  };

  return (
    <>
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          minWidth: "clamp(300px, 20vw, 650px)",
          paddingInline: "25px",
        }}
      >
        <FormProvider {...methods}>
          <CustomText
            style={{ marginBottom: "20px" }}
            fontFamily={fonts.bold}
            fontSize={14 / 8}
            textColor="#B2B2B2"
          >
            DADOS BÁSICOS DO GESTOR
          </CustomText>
          <Grid alignItems="flex-end" container spacing={5}>
            <Grid item xs={12} md={12} lg={6}>
              <Input
                readOnly={viewOnly}
                control={control}
                name="fullName"
                label="Nome Completo"
              />
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <Input
                readOnly={viewOnly || !!data?.cpf}
                control={control}
                name="cpf"
                label="CPF"
                mask={masks.CPF}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={12}>
              <Input
                readOnly={viewOnly || !!data?.email}
                control={control}
                name="email"
                label="E-Mail"
              />
            </Grid>
            {!isManager && (
              <Grid item xs={12} md={12} lg={12}>
                <InputTags
                  readOnly={viewOnly}
                  control={control}
                  name="linkToOwner"
                  label="VÍNCULO A PROPRIETÁRIO"
                  options={ownersList}
                  onChangeCustom={handleOwnerChange}
                  multiple={true}
                />
              </Grid>
            )}
            <Grid item xs={12} md={12} lg={12}>
              <InputTags
                readOnly={viewOnly}
                disabled={!isManager && !hasOwnerSelected}
                control={control}
                name="linkToStudios"
                label={
                  isManager
                    ? "VINCULAR IMÓVEIS "
                    : "VINCULAR A IMÓVEIS DO(S) PROPRIETÁRIO(S)"
                }
                options={filteredPlaces}
                multiple={true}
                placeholder={
                  !isManager && !hasOwnerSelected
                    ? "Selecione ao menos 1 proprietário"
                    : ""
                }
              />
            </Grid>
          </Grid>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "12px",
              paddingTop: "12px",
              width: "100%",
            }}
          >
            <Styles.CancelButton
              fullWidth={false}
              onClick={() => {
                customModal.close();
              }}
            >
              {viewOnly ? "Voltar" : "Cancelar"}
            </Styles.CancelButton>

            {!viewOnly && (
              <Styles.RegitryButton
                fullWidth={false}
                loading={loading}
                disabled={loading}
                onClick={() => handleSubmit(onSubmit)()}
              >
                Confirmar {data ? "Edição" : "Cadastro"}
              </Styles.RegitryButton>
            )}
          </div>
        </FormProvider>
      </div>
    </>
  );
}

export default ModalRegistry;
